import { DateService } from "@app/core/services/date.service";
import { I18nService } from "@app/core/services/i18n.service";
import { AuthenticationService } from "@app/core/services/authentication.service";
import { cloneDeep, upperFirst, toLower, capitalize } from "lodash-es";
import { AppInjector } from "@app/core/functions/app-injector";

/**
 * Adapter wrapper for all services
 * @export Adapter<T>
 */
export interface Adapter<T> {
  adapt(item: any): T;
}

export abstract class AbstractAdapter<T> implements Adapter<T> {
  private _dateService: DateService = AppInjector.get(DateService);
  private _i18nService: I18nService = AppInjector.get(I18nService);
  private _authService: AuthenticationService = AppInjector.get(AuthenticationService);

  abstract adapt(item: any): T;

  /**
   * Return date service
   */
  get dateService(): DateService {
    return this._dateService;
  }

  get authService(): AuthenticationService {
    return this._authService;
  }

  get i18nService(): I18nService {
    return this._i18nService;
  }

  /**
   * Translate text
   * @param value
   */
  translate(value: string): string {
    return this._i18nService.translate(value);
  }

  /**
   * Perform a deep clone of an object
   * @param value
   */
  clone(value: any): any {
    return cloneDeep(value);
  }

  /**
   * Convert sentences with mixed casing to a proper sentence case
   * @param value
   */
  sentenceCase(value: string): string {
    return upperFirst(toLower(value.trim()));
  }

  /**
   * Convert legacy data from server such as names and titles
   * to proper case.
   * @param value
   */
  properCase(value: string): string {
    if (!value) {
      return "";
    }

    return this._normalizeTitle(value.split(" ").map(capitalize).join(" "));
  }

  /**
   * Fix text after proper casing titles and names
   * @param value
   */
  private _normalizeTitle(value: string): string {
    const wordSeparators = /([ :,–—-])/;
    const smallWords = /^(a|an|and|as|at|but|by|en|for|if|in|nor|of|on|or|per|the|to|v.?|vs.?|via)$/i;
    const nobiliaryParticle = /^(Van|De|Der|Da|Von)$/i;
    const romanNumerals = /^(i|ii|iii|iv|v|vi|vii|viii|ix|x)$/i;
    const suffix = /^(jr|sr|junior|senior)$/i;
    const thirdLetterUpper = /^(Mc)/i;
    const inflectedNames = /^(o'b)/i;
    const nameWithDash = /[-]/g;
    const dotWithLetter = /[.]+[a-z]/g;
    const corpsLLC = /^(llc)$/i;
    const lowercaseLetters = /^[a-z]/;

    return value
      .split(wordSeparators)
      .map((current, index, array) => {
        // 1. Don't capitalize certain words
        // 2. Fix names that have van, de, der, etc.
        if (current.search(smallWords) > -1 || current.search(nobiliaryParticle) > -1) {
          if (current.length > 1 && current.search(romanNumerals) === -1) {
            return current.toLowerCase();
          }
        }

        // Fix names that have suffix like i, ii, iii, etc.
        if (current.search(romanNumerals) > -1 && array[index - 1] === " ") {
          return current.toUpperCase();
        }

        // Fix names that have suffix like sr, jr that do not have spaces after comma
        if (current.search(suffix) > -1 && array[index - 1] === ",") {
          return ` ${upperFirst(current)}`;
        }

        // Fix corporation suffixes
        if (current.toLowerCase().search(corpsLLC) > -1 && array[index - 1] === " ") {
          return current.toUpperCase();
        }

        // Fix words seperated by dots such as u.s.a => U.S.A
        if (current.toLowerCase().match(dotWithLetter)) {
          if (current.match(dotWithLetter)?.length > 1) {
            return current.toUpperCase();
          }
        }

        // Fix names seperated with dashes
        if (index > 0 && array[index - 1].match(nameWithDash)) {
          return upperFirst(current);
        }

        // Fix names with inflections such as O'brian => O'Brian
        if (inflectedNames.test(current.toLowerCase())) {
          current = current
            .split("")
            .map(function (current, index) {
              if (index === 0 || index === 2) {
                return current.toUpperCase();
              }
              return current;
            })
            .join("");
        }

        // Fix names that start with Mc
        if (thirdLetterUpper.test(current)) {
          current = current
            .split("")
            .map(function (current, index) {
              if (index === 2) {
                return current.toUpperCase();
              }
              return current;
            })
            .join("");
        }

        // Fix names not caught by proper casing due to being split by something other than a blank space
        if (current.search(lowercaseLetters) > -1) {
          return current.split(" ").map(capitalize).join(" ");
        }
        return current;
      })
      .join("");
  }
}
